/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { IFilterByStatusType, IFilterBySelectedLocationsType, TicketService } from '../ticket/ticket.service';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LocalStorageHelper } from 'src/app/plain-javascript/services/LocalStorageHelper';
import { FILTER_STATES } from 'src/app/app-data/constants/localStorageKeys';

export interface IKeyToBooleanObjMap {
	[key: string]: boolean;
}

export interface ISavedSortAndFilterData {
	sortBy: string;
	filterBy: string;
	selectedRoleIds: IKeyToBooleanObjMap;
	noLocation: boolean;
	selectedLocationsIds: IKeyToBooleanObjMap;
}

@Injectable({
	providedIn: 'root',
})
export class SideNavService {
	public checkBoxStates$ = new BehaviorSubject({
		allRolesChecked: true,
		allMyRolesChecked: true,
		someOfMyRolesChecked: false,
		someRolesChecked: false,
	});
	public scrollToToday$: Observable<boolean>;
	private scrollToTodaySub$: BehaviorSubject<boolean>;

	private activePageTitle$ = new BehaviorSubject<string>('');
	private displayBackArrowIcon$ = new BehaviorSubject<boolean>(false);
	private displayScrollToTodayIcon$ = new BehaviorSubject<boolean>(false);
	private displayFilterIcon$ = new BehaviorSubject<boolean>(false);
	private displayActiveFilterIcon$ = new BehaviorSubject<boolean>(false);

	constructor(private ticketService: TicketService, private titleService: Title) {
		this.scrollToTodaySub$ = new BehaviorSubject<boolean>(false);
		this.scrollToToday$ = this.scrollToTodaySub$.pipe(filter((b) => b === true));
	}

	public sortTickets(sortBy: string) {
		this.ticketService.sortTickets(sortBy);
	}

	public filterTickets(status: IFilterByStatusType) {
		this.ticketService.filterByStatus$.next(status);
	}

	public filterRoles(selectedRolesIds: string[]) {
		this.ticketService.filterBySelectedRoleIds$.next(selectedRolesIds);
	}

	public filterLocations(noLocation: boolean, selectedLocationsIds: string[]) {
		const allSelections = {
			noLocation,
			locationIds: selectedLocationsIds,
		} as IFilterBySelectedLocationsType;
		this.ticketService.filterBySelectedLocationIds$.next(allSelections);
	}

	public setPageTitle(title: string) {
		this.titleService.setTitle(title);
		this.activePageTitle$.next(this.titleService.getTitle());
	}

	public getPageTitle$() {
		return this.activePageTitle$;
	}

	public getDisplayBackArrowIcon$() {
		return this.displayBackArrowIcon$;
	}

	public setDisplayBackArrowIcon(value: boolean) {
		this.displayBackArrowIcon$.next(value);
	}

	public getDisplayScrollToTodayIcon$() {
		return this.displayScrollToTodayIcon$;
	}

	public setDisplayScrollToTodayIcon(value: boolean) {
		this.displayScrollToTodayIcon$.next(value);
	}

	public scrollToToday() {
		this.scrollToTodaySub$.next(true);
	}

	public getDisplayFilterIcon$() {
		return this.displayFilterIcon$;
	}

	public setDisplayFilterIcon(value: boolean) {
		this.displayFilterIcon$.next(value);
	}

	public getDisplayActiveFilterIcon$() {
		return this.displayActiveFilterIcon$;
	}

	public setDisplayActiveFilterIcon(value: boolean) {
		this.displayActiveFilterIcon$.next(value);
	}

	public writeFilterStatustoLocalStorage(
		sortByStatus: string,
		filterByStatus: string,
		selectedRoleIds: string[],
		noLocationSelected: boolean,
		selectedLocationsIds: string[]
	) {
		const projectId = this.ticketService.projectId.getValue();
		if (!projectId) {
			console.log('projectId missing');
			return;
		}
		let roleIdsObj = null;
		if (selectedRoleIds) {
			roleIdsObj = {};
			selectedRoleIds.forEach((id) => (roleIdsObj[id] = true));
		}
		let locationIdsObj = null;
		if (selectedLocationsIds) {
			locationIdsObj = {};
			selectedLocationsIds.forEach((id) => (locationIdsObj[id] = true));
		}
		const selectedFilterStates = this.readFilterStatusFromLocalStorage();
		LocalStorageHelper.write(FILTER_STATES, {
			...selectedFilterStates,
			[projectId]: {
				sortBy: sortByStatus,
				filterBy: filterByStatus,
				selectedRoleIds: roleIdsObj,
				noLocation: noLocationSelected,
				selectedLocationsIds: locationIdsObj,
			} as ISavedSortAndFilterData,
		});
		/* Uncomment to log what was written:
		const result = this.readFilterStatusFromLocalStorage();
		console.log('%c writeFilterStatustoLocalStorage', 'background: #BBBBFF', result);
		console.log('%c     written data for project', 'background: #BBBBFF', result[projectId]);
		*/
	}

	public readProjectFilterStatusFromLocalStorage(projectId: string): ISavedSortAndFilterData {
		const readData = this.readFilterStatusFromLocalStorage();
		const result = readData ? readData[projectId] : readData;
		// Uncomment to log what was read:
		// console.log('%c readProjectFilterStatusFromLocalStorage(\'' + projectId + '\')', 'background: #BBFFBB', result);
		return result;
	}

	private readFilterStatusFromLocalStorage() {
		const result = LocalStorageHelper.read(FILTER_STATES);
		// Uncomment to log what was read:
		// console.log('%c readFilterStatusFromLocalStorage', 'background: #BBFFBB', result);
		return result;
	}
}
